import { getAuthenticatedRequestHeaders } from "./Auth/serverSessionApi.ts";

interface ClientErrorPayload {
	eventType: "console" | "error" | "unhandledrejection";
	message: string;
	stack?: string;
	url: string;
}

const reported = new Map<string, number>();

const serialize = (value: unknown): string => {
	if (value instanceof Error) return value.message;
	if (typeof value === "string") return value;
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
};

const report = (payload: ClientErrorPayload): void => {
	const signature = `${payload.eventType}:${payload.message}:${payload.stack ?? ""}`;
	const now = Date.now();
	if (now - (reported.get(signature) ?? 0) < 2_000) return;
	reported.set(signature, now);
	if (reported.size > 100) {
		for (const [key, timestamp] of reported) {
			if (now - timestamp > 60_000) reported.delete(key);
		}
	}
	void fetch("/api/logs/client", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getAuthenticatedRequestHeaders(),
		},
		body: JSON.stringify(payload),
	}).catch(() => undefined);
};

export const installClientErrorReporter = (): void => {
	window.addEventListener("error", (event) => {
		report({
			eventType: "error",
			message: event.message || "Unhandled browser error",
			stack: event.error instanceof Error ? event.error.stack : undefined,
			url: window.location.href,
		});
	});
	window.addEventListener("unhandledrejection", (event) => {
		report({
			eventType: "unhandledrejection",
			message: serialize(event.reason),
			stack: event.reason instanceof Error ? event.reason.stack : undefined,
			url: window.location.href,
		});
	});

	const originalConsoleError = console.error.bind(console);
	console.error = (...values: unknown[]) => {
		originalConsoleError(...values);
		report({
			eventType: "console",
			message: values.map(serialize).join(" "),
			stack: values.find((value) => value instanceof Error)?.stack,
			url: window.location.href,
		});
	};
};
